import {useEffect, useState} from "react";
import {PortableText} from "@portabletext/react";
import {RichTextComponents} from "@/components/RichTextComponents";
import Image from "next/image";

export interface ReviewCardProps {
    image?: string;
    name?: string;
    review?: any;
}

export default function ReviewCard({ image, name, review }: ReviewCardProps) {
    const [maxLength, setMaxLength] = useState(150);

    // Funzione per troncare il testo da blocchi PortableText
    function truncateText(blocks: any[], maxLength: number) {
        if (!blocks || !Array.isArray(blocks)) {
            return [];
        }

        let currentLength = 0;
        let truncatedBlocks: any[] = [];
        let wasTruncated = false;

        for (let block of blocks) {
            if (!block.children || !Array.isArray(block.children)) {
                continue;
            }

            const blockText = block.children.map((child: any) => child.text || "").join(" ");
            const newLength = currentLength + blockText.length;

            if (newLength > maxLength) {
                const remainingChars = maxLength - currentLength;
                if (remainingChars > 0) {
                    const truncatedText = blockText.slice(0, remainingChars);
                    truncatedBlocks.push({
                        ...block,
                        children: [{
                            ...block.children[0],
                            text: truncatedText.trim() + "..."
                        }]
                    });
                }
                wasTruncated = true;
                break;
            } else {
                currentLength = newLength;
                truncatedBlocks.push(block);
            }
        }

        // Se il testo è stato troncato e non abbiamo aggiunto i puntini, aggiungili all'ultimo blocco
        if (wasTruncated && truncatedBlocks.length > 0) {
            const lastBlock = truncatedBlocks[truncatedBlocks.length - 1];
            if (lastBlock.children && lastBlock.children.length > 0) {
                const lastChild = lastBlock.children[lastBlock.children.length - 1];
                if (!lastChild.text.endsWith("...")) {
                    lastChild.text = lastChild.text.trim() + "...";
                }
            }
        }

        return truncatedBlocks;
    }

    // Funzione per troncare testo semplice (string)
    function truncateSimpleText(text: string, maxLength: number) {
        if (text.length <= maxLength) {
            return text;
        }
        return text.slice(0, maxLength).trim() + "...";
    }

    useEffect(() => {
        const updateMaxLength = () => {
            if (window.innerWidth <= 768) {
                setMaxLength(150); // Limite per mobile
            } else {
                setMaxLength(170); // Limite per desktop
            }
        };

        // Imposta il limite iniziale
        updateMaxLength();

        // Aggiunge un listener per cambiare il limite quando la finestra viene ridimensionata
        window.addEventListener("resize", updateMaxLength);

        // Pulisce l'event listener quando il componente viene smontato
        return () => window.removeEventListener("resize", updateMaxLength);
    }, []);

    // Determina il tipo di contenuto della review
    const renderReviewContent = () => {
        if (!review) {
            return <p className="text-gray-500">Nessuna recensione disponibile</p>;
        }

        // Se review è una stringa
        if (typeof review === 'string') {
            const truncatedText = truncateSimpleText(review, maxLength);
            return (
                <p className="text-gray-700 leading-relaxed">
                    {truncatedText}
                </p>
            );
        }

        // Se review è un array di blocchi PortableText
        if (Array.isArray(review)) {
            return (
                <PortableText
                    value={truncateText(review, maxLength)}
                    components={RichTextComponents}
                />
            );
        }

        // Fallback per altri tipi
        const textContent = String(review);
        return (
            <p className="text-gray-700 leading-relaxed">
                {truncateSimpleText(textContent, maxLength)}
            </p>
        );
    };

    return (
        <div className="h-80 bg-white rounded-lg shadow-card p-4 flex flex-col">
            <div className="flex flex-col items-center mb-4">
                <div className="mb-3">
                    <Image
                        className="rounded-full h-[80px] w-[80px] object-cover"
                        src={image || "/default-avatar.png"}
                        alt={name || "Review image"}
                        width={80}
                        height={80}
                    />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 text-center">
                    {name || "Utente Anonimo"}
                </h3>
            </div>

            <div className="flex-1 overflow-hidden">
                <div className="text-center text-sm md:text-base">
                    {renderReviewContent()}
                </div>
            </div>
        </div>
    );
}
